import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';

export interface SupplierFamily {
  id: string;
  code: string;
  name: string;
  supplier_id: string | null;
  /** NULL = famiglia di catalogo (condivisa); valorizzata = famiglia creata dall'org. */
  organization_id: string | null;
}

/**
 * Famiglie di prodotto visibili all'utente: quelle di catalogo (organization_id
 * NULL) più quelle create dalla propria organizzazione. La RLS filtra da sola.
 * Restituisce sia la mappa id→famiglia (per la tabella) sia l'elenco.
 */
export const useSupplierFamilies = () => {
  return useQuery({
    queryKey: ['supplier_product_families'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_product_families')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .select('id, code, name, supplier_id, organization_id' as any);
      if (error) throw new Error(`Error fetching families: ${error.message}`);
      const byId = new Map<string, SupplierFamily>();
      for (const f of (data ?? []) as unknown as SupplierFamily[]) byId.set(f.id, f);
      return byId;
    },
  });
};

/** Elenco piatto delle famiglie (comodo per i selettori). */
export const useSupplierFamiliesList = () => {
  const { data } = useSupplierFamilies();
  return Array.from(data?.values() ?? []);
};

/**
 * Crea una famiglia di prodotto della PROPRIA organizzazione.
 * Serve quando l'org vuole raggruppare i materiali a modo suo (e applicarci i
 * propri sconti) senza toccare le famiglie ufficiali del catalogo.
 */
export const useCreateOrgFamily = () => {
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { code: string; name: string; supplierId: string | null; category?: string }) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione selezionata');
      if (!input.code.trim() || !input.name.trim()) throw new Error('Codice e nome sono obbligatori');
      const { data, error } = await supabase
        .from('supplier_product_families')
        .insert({
          organization_id: currentOrganizationId,
          supplier_id: input.supplierId,
          code: input.code.trim().toUpperCase(),
          name: input.name.trim(),
          category: input.category ?? null,
          is_active: true,
        } as never)
        .select('id')
        .single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier_product_families'] });
      toast.success('Famiglia creata per la tua azienda');
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e
        ? String((e as { message: unknown }).message) : String(e);
      toast.error(`Creazione famiglia non riuscita: ${msg}`);
    },
  });
};
